/**
 * Customer composer — orchestrator for the redesigned `/sales/customers/new`.
 *
 * Owns:
 *   · The selected mode (B2B / B2C / Export).
 *   · Per-mode draft state (separate slices so switching mode doesn't
 *     lose what was typed in another).
 *   · The save flow: derive `customers.create` payload from the active
 *     mode's state, run the mutation, navigate to the new record.
 *
 * Owns the create path; the edit path is the per-section dialogs
 * under `features/customers/edit-dialogs/`, mounted from the detail
 * page (`routes/(workspace)/customers/$customerId.index.tsx`).
 *
 * @see prototype/sales/customer-new.html — V1.1 baseline this implements
 * @see docs/architecture/cookbooks.md — composer cookbook
 */
import * as React from "react";
import { Check } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { indianState, type customers } from "@flint/api-contract";
import type { z } from "zod";
import { cn } from "@flint/ui";
import { rupeesToPaiseString } from "../../../lib/format.ts";
import { BackButton } from "../../workspace/index.ts";
import { ModeCardRow, type ModeOption } from "../../../components/mode-card.tsx";
import { ComposerB2B, type ComposerB2BState } from "./composer-b2b.tsx";
import { ComposerB2C, type ComposerB2CState } from "./composer-b2c.tsx";
import { ComposerExport, type ComposerExportState } from "./composer-export.tsx";
import { ComposerTally } from "./composer-tally.tsx";

export type CustomerComposerMode = "b2b" | "b2c" | "export" | "tally";

const MODE_OPTIONS: ReadonlyArray<ModeOption<CustomerComposerMode>> = [
  {
    id: "b2b",
    fieldsCount: "1 field",
    title: "Indian business",
    sub: "B2B — paste GSTIN, we fetch legal name, state and address from GSTN.",
    recommended: true,
  },
  {
    id: "b2c",
    fieldsCount: "1 field",
    title: "Indian consumer",
    sub: "B2C — retail, walk-in or unregistered. No GSTIN required.",
  },
  {
    id: "export",
    fieldsCount: "3 fields",
    title: "Export / overseas",
    sub: "Zero-rated under LUT or with IGST refund. Section 16 IGST Act.",
  },
  {
    id: "tally",
    fieldsCount: "CSV",
    title: "From Tally / CSV",
    sub: "Bulk import — map columns, GSTN-verify, dedupe. Import from the customers list.",
  },
];

type CreateInput = z.infer<typeof customers.createInput>;
type IndianState = z.infer<typeof indianState>;

export interface CustomerComposerProps {
  /** Called on Save. Receives the derived create-input payload. */
  onSubmit: (payload: CreateInput) => Promise<void>;
  /** Saving state — disables the Save button + shows pending label. */
  pending: boolean;
  /** Server error message to surface in the footer. */
  serverError: string | null;
}

const EMPTY_B2B: ComposerB2BState = { gstin: "", lookup: null, overrides: {} };
const EMPTY_B2C: ComposerB2CState = { name: "", phone: "" };
const EMPTY_EXPORT: ComposerExportState = { name: "", country: "", email: "" };
const EMPTY_DETAILS: CustomerCreateDetailsState = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  openingBalanceRupees: "",
  openingBalanceAsOf: "",
  internalNote: "",
};

interface CustomerCreateDetailsState {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: IndianState | "";
  pincode: string;
  openingBalanceRupees: string;
  openingBalanceAsOf: string;
  internalNote: string;
}

export function CustomerComposer({ onSubmit, pending, serverError }: CustomerComposerProps) {
  const [mode, setMode] = React.useState<CustomerComposerMode>("b2b");
  const [b2b, setB2b] = React.useState<ComposerB2BState>(EMPTY_B2B);
  const [b2c, setB2c] = React.useState<ComposerB2CState>(EMPTY_B2C);
  const [exp, setExp] = React.useState<ComposerExportState>(EMPTY_EXPORT);
  const [details, setDetails] = React.useState<CustomerCreateDetailsState>(EMPTY_DETAILS);

  const payload = derivePayload(mode, b2b, b2c, exp, details);
  const canSave = payload !== null && !pending;
  const blocker = deriveBlocker(mode, b2b, b2c, exp, details);

  const handleSave = async () => {
    if (!payload) return;
    await onSubmit(payload);
  };

  return (
    // Full-height flex column — the route opts out of the shell's
    // ScrollArea (`disableViewportScroll`) and we own the only
    // scrollable region (the <main>). Header docks at the top of
    // the available viewport, footer docks at the bottom — neither
    // scrolls.
    <div className="flex flex-col h-full bg-bg">
      <ComposerHeader />

      <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5">
        <div className="max-w-[860px] mx-auto flex flex-col gap-4">
          <ModeHero mode={mode} onModeChange={setMode} />

          {mode === "b2b" ? (
            <ComposerB2B state={b2b} onChange={(p) => setB2b((s) => ({ ...s, ...p }))} />
          ) : null}
          {mode === "b2c" ? (
            <ComposerB2C state={b2c} onChange={(p) => setB2c((s) => ({ ...s, ...p }))} />
          ) : null}
          {mode === "export" ? (
            <ComposerExport state={exp} onChange={(p) => setExp((s) => ({ ...s, ...p }))} />
          ) : null}
          {mode === "tally" ? <ComposerTally /> : null}
          {mode !== "tally" ? (
            <CustomerCreateDetails
              mode={mode}
              lookupState={b2b.lookup?.state ?? null}
              state={details}
              onChange={(patch) => setDetails((current) => ({ ...current, ...patch }))}
            />
          ) : null}
        </div>
      </main>

      <ComposerFooter
        mode={mode}
        canSave={canSave}
        blocker={blocker}
        pending={pending}
        serverError={serverError}
        onSave={handleSave}
      />
    </div>
  );
}

function ComposerHeader() {
  return (
    <header className="flex-none bg-paper border-b border-line px-4 sm:px-6 py-3 flex items-center gap-3">
      <BackButton fallbackTo="/sales/customers" label="Back to customers" />
      <div className="flex flex-col min-w-0">
        <h1 className="text-base font-bold text-ink leading-tight m-0">New customer</h1>
        <p className="text-xs text-muted leading-tight">Add a buyer to your AR records.</p>
      </div>
    </header>
  );
}

function ModeHero({
  mode,
  onModeChange,
}: {
  mode: CustomerComposerMode;
  onModeChange: (m: CustomerComposerMode) => void;
}) {
  return (
    <section
      aria-label="Customer type"
      className="rounded-md border border-line bg-paper shadow-card overflow-hidden relative"
    >
      <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px] bg-sky-600" />
      <div className="px-5 pt-4 pb-3.5">
        <h2 className="text-base font-bold text-ink m-0 -tracking-[0.01em]">
          Who are you billing?
        </h2>
        <p className="text-xs text-muted mt-1 mb-3.5 leading-snug max-w-[60ch]">
          Pick once — Flint handles tax, address and invoice delivery accordingly. You can change
          this before saving.
        </p>
        <ModeCardRow value={mode} onChange={onModeChange} options={MODE_OPTIONS} />
      </div>
    </section>
  );
}

function ComposerFooter({
  mode,
  canSave,
  blocker,
  pending,
  serverError,
  onSave,
}: {
  mode: CustomerComposerMode;
  canSave: boolean;
  blocker: SaveBlocker;
  pending: boolean;
  serverError: string | null;
  onSave: () => void;
}) {
  const status = statusForMode(mode, blocker);

  return (
    <footer
      className={cn(
        "flex-none bg-paper border-t border-line",
        "px-4 sm:px-6 py-3",
        "flex items-center gap-3 flex-wrap",
      )}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {canSave ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ok-50 text-ok text-xs font-semibold border border-ok-100">
            <Check className="w-2.5 h-2.5" strokeWidth={3} />
            Ready to save
          </span>
        ) : null}
        <span className="text-xs text-muted">{status}</span>
        {serverError ? (
          <span className="text-xs text-bad font-medium" role="alert">
            {humanizeServerError(serverError)}
          </span>
        ) : null}
      </div>
      <Link
        to="/sales/customers"
        className={cn(
          "inline-flex items-center px-3 h-9 rounded-md text-sm text-ink-2",
          "hover:bg-bg",
        )}
      >
        Cancel
      </Link>
      {mode === "tally" ? null : (
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className={cn(
            "inline-flex items-center gap-1.5 h-9 px-4 rounded-md",
            "bg-ink text-paper text-sm font-semibold",
            "hover:bg-ink-2",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200",
          )}
        >
          <Check className="w-3.5 h-3.5" strokeWidth={2.4} />
          {pending ? "Saving…" : "Save customer"}
        </button>
      )}
    </footer>
  );
}

/**
 * Which reason is blocking Save right now? Drives the footer status copy
 * so users see the ACTUAL first-blocking field instead of the generic
 * mode-required message. Ordered by check precedence in `deriveBlocker`.
 */
type SaveBlocker =
  | "ready"
  | "mode_required"
  | "pincode"
  | "opening_balance"
  | "tally_signpost";

function statusForMode(mode: CustomerComposerMode, blocker: SaveBlocker): string {
  if (blocker === "tally_signpost")
    return "Bulk import — available from the customers list";

  const modeLabel =
    mode === "b2b" ? "B2B mode" : mode === "b2c" ? "B2C mode" : mode === "export" ? "Export mode" : "";

  if (blocker === "pincode") return `${modeLabel} · PIN must be 6 digits (no leading 0)`;
  if (blocker === "opening_balance")
    return `${modeLabel} · opening balance must be a rupee amount`;

  if (blocker === "ready") {
    if (mode === "b2b") return "B2B mode · GSTIN verified · ready to save";
    if (mode === "b2c") return "B2C mode · ready to save";
    if (mode === "export") return "Export mode · ready to save";
    return "";
  }
  // blocker === "mode_required"
  if (mode === "b2b") return "B2B mode · paste a 15-character GSTIN";
  if (mode === "b2c") return "B2C mode · name required";
  if (mode === "export") return "Export mode · name + country + email required";
  return "";
}

/**
 * Determine the first-blocking field for the current draft. Shared-detail
 * problems (invalid PIN, un-parseable opening balance) take precedence over
 * mode-specific missing fields — otherwise a user with a valid name but a
 * mistyped PIN would see "name required", which is misleading.
 */
function deriveBlocker(
  mode: CustomerComposerMode,
  b2b: ComposerB2BState,
  b2c: ComposerB2CState,
  exp: ComposerExportState,
  details: CustomerCreateDetailsState,
): SaveBlocker {
  if (mode === "tally") return "tally_signpost";

  // Check shared-detail fields FIRST — they apply to all non-tally modes.
  const pincode = details.pincode.trim();
  if (pincode && !/^[1-9][0-9]{5}$/.test(pincode)) return "pincode";

  const openingBalanceRaw = details.openingBalanceRupees.trim();
  if (openingBalanceRaw && rupeesToPaiseString(openingBalanceRaw) === null) {
    return "opening_balance";
  }

  // Then check mode-specific required fields.
  if (mode === "b2b") {
    if (!b2b.lookup || b2b.gstin.trim().length !== 15) return "mode_required";
    return "ready";
  }
  if (mode === "b2c") {
    if (!b2c.name.trim()) return "mode_required";
    return "ready";
  }
  if (mode === "export") {
    if (!exp.name.trim() || !exp.email.trim()) return "mode_required";
    return "ready";
  }
  return "mode_required";
}

/**
 * Derive the `customers.create` payload from the active mode's draft.
 * Returns `null` when the draft isn't valid enough to save — that
 * disables the Save button without throwing.
 *
 * Trimming + uppercasing is applied here so the contract sees the
 * same shape regardless of how the panes happened to capture input.
 */
function derivePayload(
  mode: CustomerComposerMode,
  b2b: ComposerB2BState,
  b2c: ComposerB2CState,
  exp: ComposerExportState,
  details: CustomerCreateDetailsState,
): CreateInput | null {
  const detailPayload = deriveDetailPayload(details);
  if (detailPayload === null) return null;

  if (mode === "b2b") {
    if (!b2b.lookup) return null;
    const gstin = b2b.gstin.trim().toUpperCase();
    if (gstin.length !== 15) return null;
    return cleanPayload({
      name: (b2b.overrides.displayName ?? b2b.lookup.tradeName ?? b2b.lookup.legalName).trim(),
      gstin,
      state: detailPayload.state ?? b2b.lookup.state,
      city: detailPayload.city ?? b2b.lookup.city,
      pincode: detailPayload.pincode ?? b2b.lookup.pincode,
      addressLine1: detailPayload.addressLine1 ?? b2b.lookup.address,
      addressLine2: detailPayload.addressLine2,
      contactName: b2b.overrides.contactName,
      phone: b2b.overrides.phone,
      email: b2b.overrides.email,
      isTdsApplicable: false,
      openingBalancePaise: detailPayload.openingBalancePaise,
      openingBalanceAsOf: detailPayload.openingBalanceAsOf,
      internalNote: detailPayload.internalNote,
    });
  }
  if (mode === "b2c") {
    const name = b2c.name.trim();
    if (!name) return null;
    return cleanPayload({
      name,
      phone: b2c.phone.trim() || undefined,
      isTdsApplicable: false,
      ...detailPayload,
    });
  }
  if (mode === "export") {
    const name = exp.name.trim();
    const email = exp.email.trim();
    // Country is persisted as of migration 0058. Save is still gated on
    // name + email (country defaults sensibly), but the picked country now
    // round-trips to the customer row instead of being display-only.
    if (!name || !email) return null;
    return cleanPayload({
      name,
      email,
      country: exp.country,
      isTdsApplicable: false,
      internalNote: detailPayload.internalNote,
      openingBalancePaise: detailPayload.openingBalancePaise,
      openingBalanceAsOf: detailPayload.openingBalanceAsOf,
    });
  }
  // tally mode is import-only — no inline payload to save.
  return null;
}

type DetailPayload = Omit<Partial<CreateInput>, "name">;

function deriveDetailPayload(details: CustomerCreateDetailsState): DetailPayload | null {
  const pincode = details.pincode.trim();
  if (pincode && !/^[1-9][0-9]{5}$/.test(pincode)) return null;

  const openingBalanceRaw = details.openingBalanceRupees.trim();
  const openingBalancePaise = openingBalanceRaw ? rupeesToPaiseString(openingBalanceRaw) : null;
  if (openingBalanceRaw && openingBalancePaise === null) return null;

  return {
    addressLine1: details.addressLine1.trim() || undefined,
    addressLine2: details.addressLine2.trim() || undefined,
    city: details.city.trim() || undefined,
    state: details.state || undefined,
    pincode: pincode || undefined,
    openingBalancePaise: openingBalancePaise ?? undefined,
    openingBalanceAsOf: openingBalanceRaw ? details.openingBalanceAsOf || undefined : undefined,
    internalNote: details.internalNote.trim() || undefined,
  };
}

/**
 * Strip empty strings and undefineds before sending — the customer
 * contract treats `undefined` as "not set" and rejects empty-string
 * optionals on regex-validated fields (phoneIN, email).
 */
function cleanPayload(input: PartialCreateInput): CreateInput {
  const bag: Record<string, unknown> = { name: input.name };
  for (const [key, value] of Object.entries(input)) {
    if (key === "name") continue;
    if (value === undefined || value === "" || value === null) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) continue;
      bag[key] = trimmed;
      continue;
    }
    bag[key] = value;
  }
  return bag as unknown as CreateInput;
}

interface PartialCreateInput extends Partial<CreateInput> {
  name: string;
}

function CustomerCreateDetails({
  mode,
  lookupState,
  state,
  onChange,
}: {
  mode: CustomerComposerMode;
  lookupState: string | null;
  state: CustomerCreateDetailsState;
  onChange: (patch: Partial<CustomerCreateDetailsState>) => void;
}) {
  const statePlaceholder =
    mode === "b2b" && lookupState ? `${lookupState} from GSTIN` : "Select state";
  return (
    <section
      aria-label="Customer tax address and opening balance"
      className="rounded-md border border-line bg-paper shadow-card overflow-hidden"
    >
      <header className="px-[18px] pt-3.5 pb-2 border-b border-line">
        <h3 className="text-sm font-semibold text-ink m-0">Tax, address & opening balance</h3>
      </header>

      <div className="px-[18px] py-[18px] flex flex-col gap-3.5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <TextField
            label="Address line 1"
            value={state.addressLine1}
            placeholder="MG Road"
            onChange={(value) => onChange({ addressLine1: value })}
          />
          <TextField
            label="Address line 2"
            optionalSub="optional"
            value={state.addressLine2}
            placeholder="Near Marine Drive"
            onChange={(value) => onChange({ addressLine2: value })}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
          <TextField
            label="City"
            value={state.city}
            placeholder="Ernakulam or Kozhikode"
            onChange={(value) => onChange({ city: value })}
          />
          <SelectField
            label="State"
            value={state.state}
            placeholder={statePlaceholder}
            onChange={(value) => onChange({ state: value })}
          />
          <TextField
            label="PIN code"
            value={state.pincode}
            placeholder="682011"
            inputMode="numeric"
            onChange={(value) =>
              onChange({ pincode: value.replace(/\D/g, "").slice(0, 6) })
            }
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <TextField
            label="Opening balance (Rs)"
            optionalSub="optional"
            value={state.openingBalanceRupees}
            placeholder="125000"
            inputMode="decimal"
            onChange={(value) =>
              onChange({ openingBalanceRupees: value.replace(/[^\d.,]/g, "") })
            }
          />
          <TextField
            label="Opening balance as of"
            optionalSub="optional"
            type="date"
            value={state.openingBalanceAsOf}
            placeholder=""
            disabled={!state.openingBalanceRupees.trim()}
            onChange={(value) => onChange({ openingBalanceAsOf: value })}
          />
        </div>

        <label className="flex flex-col gap-1 min-w-0">
          <span className="text-xs font-semibold text-ink-2">
            Internal note <span className="text-muted font-medium ml-1">(optional)</span>
          </span>
          <textarea
            value={state.internalNote}
            rows={3}
            placeholder="Prefers invoices on WhatsApp before dispatch."
            onChange={(e) => onChange({ internalNote: e.target.value.slice(0, 2000) })}
            className={cn(
              "w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink resize-y",
              "placeholder:text-muted",
              "hover:border-line-2",
              "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
            )}
          />
        </label>
      </div>
    </section>
  );
}

function TextField({
  label,
  optionalSub,
  value,
  placeholder,
  type = "text",
  inputMode,
  disabled,
  onChange,
}: {
  label: string;
  optionalSub?: string;
  value: string;
  placeholder: string;
  type?: React.HTMLInputTypeAttribute;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  disabled?: boolean;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">
        {label}
        {optionalSub ? <span className="text-muted font-medium ml-1">({optionalSub})</span> : null}
      </span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
          "placeholder:text-muted",
          "hover:border-line-2",
          "disabled:bg-bg disabled:text-muted disabled:cursor-not-allowed",
          "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
        )}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: IndianState | "";
  placeholder: string;
  onChange: (next: IndianState | "") => void;
}) {
  return (
    <label className="flex flex-col gap-1 min-w-0">
      <span className="text-xs font-semibold text-ink-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as IndianState | "")}
        className={cn(
          "h-9 px-3 rounded-md border border-line bg-paper text-sm text-ink",
          "hover:border-line-2",
          "focus:outline-none focus:border-sky-600 focus:ring-[3px] focus:ring-sky-100",
        )}
      >
        <option value="">{placeholder}</option>
        {indianState.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Translate tRPC/Zod error blobs into a single readable line.
 *
 * tRPC stringifies Zod errors as a JSON array of `{ path, message }`
 * objects — useful to a developer, terrifying to a user. We pick the
 * first issue, format the path as a field name, and surface the
 * message. Anything we can't parse falls through unchanged so we
 * don't silently swallow non-validation errors.
 */
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  contactName: "Contact name",
  gstin: "GSTIN",
  phone: "Phone",
  email: "Email",
  addressLine1: "Address",
  addressLine2: "Address",
  state: "State",
  city: "City",
  pincode: "PIN code",
};

function humanizeServerError(raw: string): string {
  try {
    const parsed = JSON.parse(raw) as Array<{ path?: string[]; message?: string }>;
    if (Array.isArray(parsed) && parsed.length > 0) {
      const issue = parsed[0];
      const field = issue?.path?.[0];
      const label = field ? (FIELD_LABELS[field] ?? field) : null;
      const message = issue?.message ?? "is invalid";
      return label ? `${label} — ${message}` : message;
    }
  } catch {
    // Not a JSON-shaped Zod error; fall through to the raw message.
  }
  return raw;
}
